export type Keybinding = {
  /**
   * The keys, in react-hotkeys-hook's syntax: "mod+z", "alt+mod+arrowup", "delete". A key is
   * named by its character ("z", "=") or its place ("keya", "digit1", "equal"); either way it
   * matches the key that types or prints that character (see `typesKey`). `mod` is ⌘ or Ctrl, on
   * any platform. Alternatives are separated by commas: "mod+y, mod+shift+z".
   */
  key: string
  description: string
  group: string
  /** Also fires while the focus is in a text field (undo and redo do). */
  allowWhileTyping?: boolean
  /** Left to any focused control (a button, a select…), as Tab moves the focus between them. */
  skipOnControls?: boolean
}

/** What each key prints, by its place ("KeyQ" → "a" on AZERTY), when the browser says. */
export type KeyboardLayout = ReadonlyMap<string, string>

/** A combo for react-hotkeys-hook, with the character its key is meant to type. */
export type Hotkey = { keys: string; character?: string }

const CHARACTERS: Record<string, string> = {
  equal: "=", minus: "-", bracketleft: "[", bracketright: "]", slash: "/", period: ".", comma: ",",
}

/** The character a key types, from its name in a binding: "keya" → "a", "equal" → "=". */
const characterOf = (key: string) => {
  if (/^key[a-z]$/.test(key)) return key.slice(3)
  if (/^digit[0-9]$/.test(key)) return key.slice(5)

  return key.length === 1 ? key : CHARACTERS[key]
}

const placeOf = (character: string, layout: KeyboardLayout) => (
  Array.from(layout.entries()).find(([, printed]) => printed === character)?.[0].toLowerCase()
)

/**
 * The binding's combos for react-hotkeys-hook. `mod` becomes both ⌘ and Ctrl, as the editor
 * accepts either on every platform. With the layout, a letter is also listened to where the
 * layout prints it, for ⌥ on a Mac, which changes what the key types (⌥A types å).
 */
export const toHotkeys = ({ key }: Keybinding, layout?: KeyboardLayout): Hotkey[] => key
  .split(",")
  .map((combo) => combo.trim())
  .flatMap((combo) => {
    const parts = combo.split("+")
    const last = parts[parts.length - 1]
    const character = characterOf(last)
    const place = layout && character && /^key[a-z]$/.test(last) ? placeOf(character, layout) : undefined
    const combos = place && place !== last ? [combo, [...parts.slice(0, -1), place].join("+")] : [combo]

    return combos
      .flatMap((variant) => (
        variant.split("+").includes("mod")
          ? [variant.replace("mod", "meta"), variant.replace("mod", "ctrl")]
          : [variant]
      ))
      .map((keys) => ({ keys, character }))
  })

/**
 * Whether a press is meant for a combo whose key types `character`: it types it, or the layout
 * prints it on that key. When neither is known (Shift or ⌥ change the character and the browser
 * doesn't give the layout, or no key prints it), the key's place decides.
 */
export const typesKey = (event: KeyboardEvent, character?: string, layout?: KeyboardLayout) => {
  if (character === undefined) return true

  const typed = event.key.length === 1 && /[!-~]/.test(event.key) ? event.key.toLowerCase() : undefined
  const printed = layout?.get(event.code)?.toLowerCase()
  if (typed === character || printed === character) return true
  if (layout && printed !== undefined) return placeOf(character, layout) === undefined

  return typed === undefined || event.shiftKey
}

const TYPING_ROLES = ["textbox", "searchbox", "combobox"]
// Controls that use plain keys themselves: arrows, Space, Enter, Esc
const KEYED_ROLES = ["menuitem", "menuitemradio", "menuitemcheckbox", "option", "listbox", "slider", "spinbutton"]
const CONTROL_ROLES = ["button", "tab", "switch", "checkbox", "radio", "link", ...KEYED_ROLES]

const asElement = (target: EventTarget | null) => (
  target instanceof HTMLElement ? target : null
)

/** Whether the focus is somewhere the user types. */
export const isTypingTarget = (target: EventTarget | null) => {
  const element = asElement(target)

  return !!element && (
    element.tagName === "INPUT"
    || element.tagName === "TEXTAREA"
    || element.isContentEditable === true
    || TYPING_ROLES.includes(element.getAttribute("role") ?? "")
  )
}

/** Whether the focus is on a menu item, a select or another control that uses plain keys. */
const isKeyedControl = (target: EventTarget | null) => {
  const element = asElement(target)

  return !!element && (
    element.tagName === "SELECT" || KEYED_ROLES.includes(element.getAttribute("role") ?? "")
  )
}

/** Whether the focus is on any control: a field, a button, a link, a menu item… */
export const isControlTarget = (target: EventTarget | null) => {
  const element = asElement(target)

  return !!element && (
    isTypingTarget(element)
    || isKeyedControl(element)
    || ["BUTTON", "SELECT"].includes(element.tagName)
    || (element.tagName === "A" && element.hasAttribute("href"))
    || CONTROL_ROLES.includes(element.getAttribute("role") ?? "")
  )
}
